/**
 * Single source of truth for the "Become a member & pay now" checkout. The
 * payment cards and the sidebar all read this so the cart, items, and pricing
 * are identical. `startCheckout` opens the embedded Stripe Payment Element
 * modal (no hosted-Checkout redirect) — the modal renders our own localized
 * order summary and uses Stripe only to collect payment.
 */
import { useBookingFlow } from "@/hooks/useBookingFlow";
import {
  buildCheckoutItems,
  computeBloodMembershipPricing,
  computeBundlePricing,
  isScanPackage,
} from "@/utils/bloodMembershipPricing";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import { useEmbeddedPaymentStore } from "@/stores/embeddedPaymentStore";
import {
  CADENCE_TIER_ID,
  CADENCE_TIER_METADATA_ID,
  CADENCE_PLAN_SLUG,
  getCadencePriceChf,
} from "@/utils/membershipPricing";

export function useBloodCheckout() {
  const { selectedProductDetails, selectedAddOnDetails, language } =
    useBookingFlow();
  const setEmbeddedPaymentOpen = useEmbeddedPaymentStore((s) => s.setOpen);
  const captureBookingAttempt = useEmbeddedPaymentStore(
    (s) => s.captureBookingAttempt,
  );

  // The membership is billed at the chosen cadence tier (once 299 / twice 499 /
  // four 899) — NOT a flat CHF 299. The plan applies wherever the membership is
  // offered (the dedicated cadence step AND the payment-step option card), so it
  // is keyed off the selected `plan` (defaults to baseline), not the cadence-step
  // `active` flag. The tier product id is the Stripe source of truth.
  const membershipPlan = useMembershipPlanStore((s) => s.plan);
  const membershipTiers = useMembershipTiers().tiers;
  const cadenceTierProductId = membershipTiers?.find(
    (t) => t.id === CADENCE_TIER_ID[membershipPlan],
  )?.stripe_product_id;
  // Mode-agnostic handle for the same tier — the edge function resolves it to
  // the account-correct product id (prod vs sandbox), so checkout works in both.
  const cadenceTierMetadataId = CADENCE_TIER_METADATA_ID[membershipPlan];
  // ahead_mono planSlug for the chosen cadence — forwarded to mono so it
  // provisions the right entitlements after payment.
  const cadencePlanSlug = CADENCE_PLAN_SLUG[membershipPlan];
  // The membership price the customer is shown — also sent to the edge function
  // so it can refuse the checkout if Stripe's recurring price differs (no silent
  // display↔charge drift). Same resolver the sidebar/order summary use.
  const expectedMembershipCents =
    getCadencePriceChf(membershipPlan, membershipTiers) * 100;

  // The full cart (products + add-ons) is always read from the store.
  const cart = [...selectedProductDetails, ...selectedAddOnDetails];
  // Two totals power the payment card:
  //  - Pay later  = the full standalone price of THIS booking (one blood test +
  //    add-ons), independent of cadence — the blood test at its CHF 299 default.
  //  - Membership = the selected cadence subscription (299 / 499 / 899) +
  //    member-priced add-ons. This is what reacts to the "Change cadence" picker.
  // Savings is shown only when joining costs no more than paying later for the
  // same single appointment (i.e. the baseline cadence) — the badge hides itself
  // for higher cadences, which legitimately add follow-up tests.
  const standalone = computeBloodMembershipPricing(cart);
  const membership = computeBloodMembershipPricing(
    cart,
    expectedMembershipCents,
  );
  const pricing = {
    fullTotalCents: standalone.fullTotalCents,
    memberTotalCents: membership.memberTotalCents,
    // The cadence-priced invoice lines behind memberTotalCents — what voucher
    // math must run over (Stripe rounds a percentage coupon per line).
    memberLines: membership.memberLines,
    savingsCents: Math.max(
      0,
      standalone.fullTotalCents - membership.memberTotalCents,
    ),
  };

  // When the cart is a scan + membership bundle, compute the one-time total
  // (netted scan + member-priced add-ons) so the edge function can refuse a
  // session whose charge would differ from what the summary showed.
  const scanPackage = selectedProductDetails.find(isScanPackage);
  const bundle = scanPackage
    ? computeBundlePricing(
        scanPackage,
        selectedAddOnDetails,
        expectedMembershipCents,
      )
    : null;
  const expectedOneTimeCents = bundle ? bundle.oneTimeCents : undefined;

  // The exact items the edge function needs to build the subscription + add-on
  // invoice items. Exposed so the embedded payment modal can submit them.
  const items = buildCheckoutItems(
    selectedProductDetails,
    selectedAddOnDetails,
  );

  // Open the embedded payment modal instead of redirecting to hosted Checkout.
  const startCheckout = () => {
    // Edge-independent last resort BEFORE the popup: durably capture the booking
    // (direct PostgREST → booking_submission_attempts) so the customer's data
    // survives even if create-blood-checkout is slow/down or a redirect payment
    // method (TWINT/Klarna/PayPal) never returns.
    captureBookingAttempt?.();
    setEmbeddedPaymentOpen(true);
  };

  return {
    startCheckout,
    // No redirect latency anymore, so the CTA never needs a loading state.
    loading: false,
    cart,
    pricing,
    items,
    expectedMembershipCents,
    expectedOneTimeCents,
    bundle,
    cadenceTierProductId,
    cadenceTierMetadataId,
    cadencePlanSlug,
    language,
  };
}
